import { useState } from 'react'
import { Modal } from '../Common/Modal'
import { Button } from '../Common/Button'

export function ProductProviderImport({ onImport, onClose, sidebarCollapsed, isDarkMode = false }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [columnMap, setColumnMap] = useState({})

  const AUTO_MAP = {
    'name': 'name',
    'provider': 'name',
    'provider name': 'name',
    'contact count': 'contact_count',
    'contacts': 'contact_count',
    'active': 'active',
  }

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    setError('')
    setFile(selectedFile)
    setLoading(true)
    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)
          if (rows.length === 0) { setError('No data found in file'); return }

          const headers = Object.keys(rows[0])
          const newMap = {}
          headers.forEach(header => {
            const matched = AUTO_MAP[header.toLowerCase().trim()]
            if (matched) newMap[header] = matched
          })
          setColumnMap(newMap)
          setPreview(rows.slice(0, 5))
        } catch (err) {
          setError(`Error reading file: ${err.message}`)
        }
      }
      reader.readAsBinaryString(selectedFile)
    } catch (err) {
      setError(`Error loading Excel library: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async () => {
    if (!file || !preview) { setError('No file selected'); return }
    setError('')
    setLoading(true)
    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()
      reader.onload = async (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)

          const mapped = rows.map(row => {
            const out = {}
            Object.entries(columnMap).forEach(([excelCol, dbCol]) => {
              if (!dbCol || dbCol === 'skip' || row[excelCol] === undefined) return
              if (dbCol === 'active') {
                out[dbCol] = row[excelCol] === true || row[excelCol] === 'TRUE' || row[excelCol] === 'Yes' || row[excelCol] === 1
              } else if (dbCol === 'contact_count') {
                out[dbCol] = Number(row[excelCol]) || 0
              } else {
                out[dbCol] = String(row[excelCol]).trim()
              }
            })
            return out
          }).filter(row => row.name)

          if (mapped.length === 0) {
            setError('No valid rows found. Ensure the Name column is mapped.')
            setLoading(false)
            return
          }

          await onImport(mapped)
          setLoading(false)
        } catch (err) {
          setError(`Import error: ${err.message}`)
          setLoading(false)
        }
      }
      reader.onerror = () => { setError('Error reading file'); setLoading(false) }
      reader.readAsBinaryString(file)
    } catch (err) {
      setError(`Error: ${err.message}`)
      setLoading(false)
    }
  }

  const excelColumns = preview ? Object.keys(preview[0]) : []

  return (
    <Modal isOpen={true} onClose={onClose} title="Import Product Providers" sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode}>
      <div className="space-y-4 max-h-[80vh] flex flex-col">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {!preview ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              type="file"
              id="provider-file-input"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              disabled={loading}
              className="hidden"
            />
            <label htmlFor="provider-file-input" className="cursor-pointer">
              <p className="text-lg font-semibold text-sfp-darkgray mb-2">
                📁 Drop file here or click to select
              </p>
              <p className="text-sm text-gray-600">Supported: Excel (.xlsx, .xls) or CSV</p>
              {loading && <p className="text-sm text-sfp-orange mt-2">Loading...</p>}
            </label>
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-y-auto">
            <div>
              <h3 className="font-semibold text-sm text-sfp-darkgray mb-3">Map Excel Columns to Fields</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {excelColumns.map(col => (
                  <div key={col} className="flex gap-2 items-center text-sm">
                    <span className="flex-1 text-gray-700">{col}</span>
                    <span className="text-gray-400">→</span>
                    <select
                      value={columnMap[col] || ''}
                      onChange={(e) => setColumnMap(prev => ({ ...prev, [col]: e.target.value }))}
                      className="input-field flex-1 text-sm py-1"
                    >
                      <option value="">Skip</option>
                      <option value="name">Name</option>
                      <option value="contact_count">Contact Count</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-sfp-darkgray mb-2">Preview (first 5 rows)</h3>
              <div className="overflow-x-auto border border-gray-200 rounded max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="table-header sticky top-0">
                    <tr>{excelColumns.map(col => <th key={col} className="px-3 py-2 text-left">{col}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                        {excelColumns.map(col => (
                          <td key={col} className="px-3 py-2 text-gray-700">
                            {String(row[col] ?? '').substring(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-1">Showing first 5 rows</p>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} type="button" disabled={loading}>Cancel</Button>
          {preview && (
            <>
              <Button variant="secondary" onClick={() => { setPreview(null); setFile(null); setColumnMap({}) }} type="button" disabled={loading}>
                Back
              </Button>
              <Button variant="primary" onClick={handleImport} disabled={loading}>
                {loading ? 'Importing...' : 'Import Providers'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
